import React from 'react';
import {
  Page, Text, View, Document, StyleSheet, Font
} from '@react-pdf/renderer';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { getConnectionDiameterByCode } from 'utils/codes';

const styles = StyleSheet.create({
  header: {
    color: '#011640',
    fontSize: 12,
    marginBottom: 20,
    textAlign: 'center',
  },
  page: {
    paddingTop: 15,
    paddingBottom: 15,
    paddingHorizontal: 25,
  },
  section: {
    padding: 10,
  },
  image: {
    marginVertical: 15,
    marginHorizontal: 100,
  },
  title: {
    fontSize: 25,
    textAlign: 'center',
    fontFamily: 'OpenSansBold'
  },
  subtitle: {
    fontSize: 16,
    margin: 6,
    fontFamily: 'OpenSans',
    textDecoration: 'underline',
  },
  subtitleLevelTwo: {
    fontSize: 14,
    margin: 6,
    fontFamily: 'OpenSans',
    textDecoration: 'underline',
  },
  codes: {
    fontSize: 24,
    margin: 6,
    fontFamily: 'OpenSans'
  },
  text: {
    margin: 5,
    fontSize: 12,
    textAlign: 'justify',
    fontFamily: 'OpenSans'
  },
  textprime: {
    paddingTop: '20px',
    margin: 5,
    fontSize: 12,
    textAlign: 'justify',
    fontFamily: 'OpenSans'
  },
  message: {
    color: 'orange',
    fontWeight: 'bold',
    fontSize: 12,
  }
});

Font.register({
  family: 'Oswald',
  src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf'
});

Font.register({
  family: 'OpenSans',
  src: 'https://fonts.gstatic.com/s/opensans/v27/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsjZ0B4gaVQUwaEQbjA.woff'
});

Font.register({
  family: 'OpenSansBold',
  src: 'https://fonts.gstatic.com/s/opensans/v27/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsg-1x4gaVQUwaEQbjA.woff'
});

const SpecificationPDF = ({
  codes, details, message, id, recipe, products
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.section}>
        <Text style={styles.title} break>
          Cahier des charges achat matériaux en contact
          avec des denrées alimentaires.
        </Text>
        <Text style={styles.textprime} break>
          Référence du cahier des charges:
          {' '}
          {id}
        </Text>
        <Text style={styles.text} break>
          Nom de la recette:
          {' '}
          {recipe}
        </Text>
        <Text style={styles.text} break>
          Le produit que vous nous proposez doit être conforme avec l’article 16 du règlement CE N° 1935/2004 à
          ce titre vous devrez attester que les tests de migrations effectués sur les matériaux et matériels en
          contact avec les denrées alimentaires est conforme pour les simulants suivants :
        </Text>
        <Text style={styles.codes} break>
          {codes}
        </Text>
        {message !== '' && (
        <Text style={styles.message} break>
          {message}
        </Text>
        )}
      </View>
      {details?.cdcType && (
      <>
        <View style={styles.section}>
          <Text style={styles.subtitle}>Condition NEP</Text>
          {details?.warmWatter && details?.warmWatterTemperature && (
          <Text style={styles.text} break>
            Eau chaude:
            {' '}
            {`Température: ${details?.warmWatterTemperature} °C`}
            {', '}
            {`Durée: ${details?.warmWatterDuration} minutes`}
          </Text>
          )}
          {details?.steam && details?.steamTemperature && (
          <Text style={styles.text} break>
            Vapeur circuit ouvert:
            {' '}
            {`Température: ${details?.steamTemperature} °C`}
            {', '}
            {`Durée: ${details?.steamDuration} minutes`}
          </Text>
          )}
          {details?.hno3 && details?.hno3Concentration && (
          <Text style={styles.text} break>
            Acide nitrique (HNO3):
            {' '}
            {`Concentration: ${details?.hno3Concentration} %`}
            {', '}
            {`Température: ${details?.hno3Temperature} °C`}
            {', '}
            {`Durée: ${details?.hno3Duration} minutes`}
          </Text>
          )}
          {details?.h3po4 && details?.h3po4Concentration && (
          <Text style={styles.text} break>
            Acide phosphorique (H3PO4):
            {' '}
            {`Concentration: ${details?.h3po4Concentration} %`}
            {', '}
            {`Température: ${details?.h3po4Temperature} °C`}
            {', '}
            {`Durée: ${details?.h3po4Duration} minutes`}
          </Text>
          )}
          {details?.cc && details?.ccConcentration && (
          <Text style={styles.text} break>
            Composés chlorés:
            {' '}
            {`Concentration: ${details?.ccConcentration} %`}
            {', '}
            {`Température: ${details?.ccTemperature} °C`}
            {', '}
            {`Durée: ${details?.ccDuration} minutes`}
          </Text>
          )}
          {details?.naoh && details?.naohConcentration && (
          <Text style={styles.text} break>
            Hydroxyde de sodium (NaOH):
            {' '}
            {`Concentration: ${details?.naohConcentration} %`}
            {', '}
            {`Température: ${details?.naohTemperature} °C`}
            {', '}
            {`Durée: ${details?.naohDuration} minutes`}
          </Text>
          )}
          {details?.h2o2 && details?.h2o2Concentration && (
          <Text style={styles.text} break>
            Peroxyde d&apos;hydrogène (H2O2):
            {' '}
            {`Concentration: ${details?.h2o2Concentration} %`}
            {', '}
            {`Température: ${details?.h2o2Temperature} °C`}
            {', '}
            {`Durée: ${details?.h2o2Duration} minutes`}
          </Text>
          )}
          {details?.c2h4o3 && details?.c2h4o3Concentration && (
          <Text style={styles.text} break>
            Acide peracétique ( C2H4O3 ):
            {' '}
            {`Concentration: ${details?.c2h4o3Concentration} %`}
            {', '}
            {`Température: ${details?.c2h4o3Temperature} °C`}
            {', '}
            {`Durée: ${details?.c2h4o3Duration} minutes`}
          </Text>
          )}
        </View>
        <View style={styles.section}>
          <Text style={styles.subtitle}>Cycles</Text>
          <Text style={styles.text} break>
            {`Nombre de cycle(s): ${details?.numberCycles}`}
          </Text>
          {details?.NepNeedValidation && (
            <Text style={styles.text} break>
              Nécessite une validation ?
              {' '}
              {details?.NepNeedValidation === 'yes' ? 'Oui' : 'Non'}
            </Text>
          )}
        </View>
        <View style={styles.section}>
          <Text style={styles.subtitle}>Normes</Text>
          <Text style={styles.text} break>
            Est impactée par les trois impératifs normatifs à respecter
            (Bonnes pratiques, UE règlementation, FDA règlementation):
            {' '}
            {details?.hasGoodPractice ? 'Oui' : 'Non'}
          </Text>
          {!_.isEmpty(details.standards) && (
            <Text style={styles.text} break>
              Est impactée par un des trois impératifs normatifs spécifique suivants:
              {' '}
              {details?.standards.join()}
            </Text>
          )}
          {!_.isEmpty(details.environmentalConstraints) && (
            <Text style={styles.text} break>
              Est impactée par une des trois contraintes environnementales suivantes:
              {' '}
              {details?.environmentalConstraints.join()}
            </Text>
          )}
        </View>
        <View style={styles.section}>
          <Text style={styles.subtitle}>Informations utilisation</Text>
          {details?.pressure && (
          <Text style={styles.text} break>
            Pression
            :
            {' '}
            {details?.pressure}
            {' '}
            bar
          </Text>
          )}
          {details?.depressure && (
          <Text style={styles.text} break>
            Dépression
            :
            {' '}
            {details?.depressure}
            {' '}
            bar
          </Text>
          )}
          {details?.useTemperature && (
          <Text style={styles.text} break>
            Température d’utilisation
            :
            {' '}
            {details?.useTemperature}
            {' '}
            °C
          </Text>
          )}
        </View>
        <View style={styles.section}>
          <Text style={styles.subtitle}>Caractéristiques du flexible</Text>
          {details?.r1Type && (
            <>
              <Text style={styles.subtitleLevelTwo}>Raccord 1</Text>
              <Text style={styles.text} break>
                Raccord1 type
                :
                {' '}
                {details?.r1Type}
              </Text>
            </>
          )}
          {details?.r1Diameter && (
          <Text style={styles.text} break>
            Raccord1 diamètre
            :
            {' '}
            {getConnectionDiameterByCode(details?.r1Type.split(' ')[0], details?.r1Diameter)}
            {' '}
            mm
          </Text>
          )}
          <Text style={styles.text} break>
            {`Coude: ${details?.r1RightAngled === 0 ? 'Non' : details?.r1RightAngled}`}
            {details?.r1RightAngled !== 0 ? ' %' : ''}
          </Text>
          {details?.r1asherNumber > 0 && (
            <Text style={styles.text} break>
              {`${details?.r1asherNumber} rondelle(s) ${details?.r1WasherColor === 'yellow' ? ' couleur jaune' : ' couleur rouge'}`}
            </Text>
          )}
          {products?.w1 && (
            <Text style={styles.text} break>
              {`Code rondelle(s): ${products?.w1?.sku}`}
            </Text>
          )}
          {products?.r1 && (
            <Text style={styles.text} break>
              {`Code raccord 1: ${products?.r1?.sku}`}
            </Text>
          )}
          {details?.r2Type && (
            <>
              <Text style={styles.subtitleLevelTwo}>Raccord 2</Text>
              <Text style={styles.text} break>
                Raccord2 type
                :
                {' '}
                {details?.r2Type}
              </Text>
            </>
          )}
          {details?.r2Diameter && (
          <Text style={styles.text} break>
            Raccord2 diamètre
            :
            {' '}
            {getConnectionDiameterByCode(details?.r2Type.split(' ')[0], details?.r2Diameter)}
            {' '}
            mm
          </Text>
          )}
          <Text style={styles.text} break>
            {`Coude: ${details?.r2RightAngled === 0 ? 'Non' : details?.r2RightAngled}`}
            {details?.r2RightAngled !== 0 ? ' %' : ''}
          </Text>
          {details?.r2asherNumber > 0 && (
            <Text style={styles.text} break>
              {`${details?.r2asherNumber} rondelle(s) ${details?.r2WasherColor === 'yellow' ? ' couleur jaune' : ' couleur rouge'}`}
            </Text>
          )}
          {products?.w2 && (
            <Text style={styles.text} break>
              {`Code rondelle(s): ${products?.w2?.sku}`}
            </Text>
          )}
          {products?.r2 && (
            <Text style={styles.text} break>
              {`Code raccord 2: ${products?.r2?.sku}`}
            </Text>
          )}
          {details?.length && (
            <>
              <Text style={styles.subtitleLevelTwo}>Flexible</Text>
              <Text style={styles.text} break>
                Longueur du flexible de joint à joint
                :
                {' '}
                {details?.length}
                {' '}
                mm
              </Text>
            </>
          )}
          {details?.diameter && (
          <Text style={styles.text} break>
            Diamètre du flexible
            :
            {' '}
            {details?.diameter}
            {' '}
            mm
          </Text>
          )}
          {products?.flexible && (
            <>
              <Text style={styles.text} break>
                {`Nom: ${products?.flexible?.name}`}
              </Text>
              <Text style={styles.text} break>
                {`Code: ${products?.flexible?.sku}`}
              </Text>
            </>
          )}
        </View>
      </>
      )}
      <Text style={styles.header} fixed>
        ~ Généré par www.calculateur.flex-key.com ~
      </Text>
    </Page>
  </Document>
);

SpecificationPDF.propTypes = {
  codes: PropTypes.string,
  message: PropTypes.string,
  id: PropTypes.string,
  recipe: PropTypes.string,
  details: PropTypes.shape({
    cdcType: PropTypes.string,
    nep: PropTypes.shape({}),
    isFlexibleSpecifications: PropTypes.string,
    warmWatter: PropTypes.bool,
    warmWatterTemperature: PropTypes.number,
    warmWatterDuration: PropTypes.number,
    steam: PropTypes.bool,
    steamTemperature: PropTypes.number,
    steamDuration: PropTypes.number,
    hno3: PropTypes.bool,
    hno3Temperature: PropTypes.number,
    hno3Concentration: PropTypes.number,
    hno3Duration: PropTypes.number,
    h3po4: PropTypes.bool,
    h3po4Temperature: PropTypes.number,
    h3po4Duration: PropTypes.number,
    h3po4Concentration: PropTypes.number,
    cc: PropTypes.bool,
    ccTemperature: PropTypes.number,
    ccDuration: PropTypes.number,
    ccConcentration: PropTypes.number,
    naoh: PropTypes.bool,
    naohConcentration: PropTypes.number,
    naohDuration: PropTypes.number,
    naohTemperature: PropTypes.number,
    h2o2: PropTypes.bool,
    h2o2Concentration: PropTypes.number,
    h2o2Duration: PropTypes.number,
    h2o2Temperature: PropTypes.number,
    c2h4o3: PropTypes.bool,
    c2h4o3Concentration: PropTypes.number,
    c2h4o3Duration: PropTypes.number,
    c2h4o3Temperature: PropTypes.number,
    pressure: PropTypes.number,
    raccord1: PropTypes.string,
    raccord2: PropTypes.string,
    length: PropTypes.string,
    diameter: PropTypes.string,
    depressure: PropTypes.number,
    useTemperature: PropTypes.number,
    r1Type: PropTypes.string,
    r1Diameter: PropTypes.number,
    r2Type: PropTypes.string,
    r2Diameter: PropTypes.number,
    numberCycles: PropTypes.number,
    NepNeedValidation: PropTypes.string,
    hasGoodPractice: PropTypes.bool,
    standards: PropTypes.arrayOf(PropTypes.string),
    environmentalConstraints: PropTypes.arrayOf(PropTypes.string),
    r1RightAngled: PropTypes.number,
    r2RightAngled: PropTypes.number,
    r1asherNumber: PropTypes.number,
    r2asherNumber: PropTypes.number,
    r1WasherColor: PropTypes.string,
    r2WasherColor: PropTypes.string,
  }),
  products: PropTypes.arrayOf(
    PropTypes.arrayOf({
      r1: PropTypes.shape({
        codes: PropTypes.string,
      }),
      r2: PropTypes.shape({
        codes: PropTypes.string,
      })
    })
  )
};

export default SpecificationPDF;
